// CB:BEGIN admin-contractors-list
import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { FaSearch, FaTrash } from 'react-icons/fa';
// import { useUser } from '@clerk/clerk-react';
import { useApi } from '@/lib/useApi';
import AdminContractorView from './AdminContractorView';
import AdminEditContractorDashboard from './AdminEditContractorDashboard';
import AdminEditContractorProfile from './AdminEditContractorProfile';
// CB:BEGIN contractor-documents-import
import ContractorDocumentsModal from '../../components/admin/ContractorDocumentsModal';
// CB:END contractor-documents-import
// CB:BEGIN import-bids-import
import { ImportBidsModal } from '../../components/admin/ImportBidsModal';
// CB:END import-bids-import
// CB:BEGIN bulk-operations-import
import { BulkOperationsPanel } from '../../components/admin/BulkOperationsPanel';
// CB:END bulk-operations-import

interface Contractor {
  id: string;
  company?: string;
  verified: boolean;
  stripeAccountId?: string; // CB:BEGIN stripe-account-field
  bidFeeOverride?: 'DEFAULT' | 'NO_BID_FEE' | 'BID_FEE'; // CB:BEGIN bid-fee-override-interface
  user: {
    email: string;
    phone?: string;
    createdAt: string;
  };
  bids: Array<{
    id: string;
    amount: number;
    status: string;
    createdAt: string;
  }>;
  verification?: {
    licenseNumber?: string;
    insuranceProofUrl?: string;
    w9Url?: string;
    businessName?: string;
    businessAddress?: string;
  };
  _count: {
    bids: number;
  };
}

interface ContractorsResponse {
  contractors: Contractor[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const AdminContractorsList: React.FC<{ onNavigate?: (screen: string) => void }> = ({ onNavigate }) => {
  // Clerk user is not currently used on this page
  const api = useApi();
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'view' | 'edit-profile' | 'edit-dashboard'>('list');
  // CB:BEGIN contractor-documents-state
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [selectedContractorForDocuments, setSelectedContractorForDocuments] = useState<Contractor | null>(null);
  // CB:END contractor-documents-state
  // CB:BEGIN import-bids-state
  const [importBidsModalOpen, setImportBidsModalOpen] = useState(false);
  const [selectedContractorForBids, setSelectedContractorForBids] = useState<Contractor | null>(null);
  // CB:END import-bids-state
  // CB:BEGIN bulk-operations-state
  const [selectedContractorIds, setSelectedContractorIds] = useState<string[]>([]);
  // CB:END bulk-operations-state

  // CB:BEGIN admin-contractors-filter-fix
  const fetchContractors = async (page = 1, search = '', verification = '') => {
    try {
      setLoading(true);
      console.log('🔍 Fetching contractors...');
      const verificationParam = verification === 'verified' ? 'true' : (verification === 'unverified' ? 'false' : '');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
        ...(verificationParam && { verification: verificationParam }),
      });
      const data = await api.get<ContractorsResponse>(`/api/admin/contractors?${params}`);
      console.log('✅ Contractors data received:', data);
      
      // CB:BEGIN defensive-contractors-response
      // Handle 403 errors and malformed responses
      if (data && 'error' in data) {
        throw new Error(data.error || 'Forbidden');
      }
      if (!data || !Array.isArray(data.contractors)) {
        console.warn('⚠️ Invalid contractors response:', data);
        setContractors([]);
        setError('Invalid response format');
        return;
      }
      // CB:END defensive-contractors-response
      
      setContractors(data.contractors || []);
      setTotalPages(data.pagination?.pages || 1);
      setCurrentPage(data.pagination?.page || 1);
    } catch (err) {
      console.error('❌ Error fetching contractors:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch contractors');
      setContractors([]); // Ensure empty array on error
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  // CB:END admin-contractors-filter-fix

  useEffect(() => {
    fetchContractors(currentPage, searchTerm, verificationFilter);
  }, [currentPage, searchTerm, verificationFilter]);

  const handleVerifyContractor = async (contractorId: string, verified: boolean) => {
    const contractor = contractors.find(c => c.id === contractorId);
    const companyName = contractor?.company || 'Unknown Company';
    const action = verified ? 'verify' : 'unverify';
    
    const confirmed = window.confirm(
      `Are you sure you want to ${action} contractor ${companyName}?\n\n` +
      `This will ${verified ? 'mark them as verified' : 'remove their verification status'}.`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      await api.patch(`/api/admin/contractors/${contractorId}/verify`, { verified });
      
      // Update the contractor in the local state immediately for better UX
      setContractors(prev => prev.map(c => 
        c.id === contractorId ? { ...c, verified } : c
      ));
      
      console.log(`✅ Contractor ${companyName} ${action}d successfully`);
    } catch (err) {
      console.error(`Error ${action}ing contractor:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} contractor`);
      // Refresh the list in case of error to ensure we have the latest data
      await fetchContractors(currentPage, searchTerm, verificationFilter);
    }
  };

  const handleDeleteContractor = async (contractor: Contractor) => {
    const companyName = contractor.company || 'Unknown Company';
    const bidCount = contractor.bids.length;
    
    const confirmed = window.confirm(
      `Are you sure you want to PERMANENTLY DELETE contractor ${companyName}?\n\n` +
      `This will also delete:\n` +
      `• ${bidCount} bid${bidCount !== 1 ? 's' : ''}\n` +
      `• Their user account\n\n` +
      `This action CANNOT be undone!`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      const response = await api.delete(`/api/admin/contractors/${contractor.id}`);
      
      // Remove the contractor from the local state immediately for better UX
      setContractors(prev => prev.filter(c => c.id !== contractor.id));
      
      // Also refresh the data from the server to ensure consistency
      await fetchContractors(currentPage, searchTerm, verificationFilter);
      
      console.log(`✅ Contractor ${companyName} deleted successfully`);
      console.log(`🗑️ Deleted ${response.deletedCounts?.bids || 0} bids`);
    } catch (err) {
      console.error('Error deleting contractor:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete contractor');
      // Refresh the list in case of error to ensure we have the latest data
      await fetchContractors(currentPage, searchTerm, verificationFilter);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchContractors(1, searchTerm, verificationFilter);
  };

  const handleViewContractor = (contractor: Contractor) => {
    console.log('🔍 handleViewContractor called for:', contractor.id);
    setSelectedContractor(contractor);
    setViewMode('view');
  };

  const handleEditContractorProfile = (contractor: Contractor) => {
    setSelectedContractor(contractor);
    setViewMode('edit-profile');
  };

  const handleEditContractorDashboard = (contractor: Contractor) => {
    console.log('🔍 handleEditContractorDashboard called for:', contractor.id);
    setSelectedContractor(contractor);
    setViewMode('edit-dashboard');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedContractor(null);
    // Refresh the contractors list to show updated data
    fetchContractors(currentPage, searchTerm, verificationFilter);
  };

  // const handleSaved = () => {
  //   setViewMode('view');
  //   fetchContractors(currentPage, searchTerm, verificationFilter);
  // };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  console.log('🔍 AdminContractorsList render state:', { 
    loading, 
    contractors: contractors.length, 
    error,
    currentPage,
    searchTerm,
    verificationFilter
  });

  if (loading && contractors.length === 0) {
    console.log('🔄 Showing loading spinner');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Render different views based on mode
  if (viewMode === 'view' && selectedContractor) {
    return (
      <AdminContractorView 
        contractorId={selectedContractor.id}
        onNavigate={(screen: string) => {
          if (screen === 'admin-contractors-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  if (viewMode === 'edit-profile' && selectedContractor) {
    return (
      <AdminEditContractorProfile
        contractorId={selectedContractor.id}
        onNavigate={(screen: string) => {
          if (screen === 'admin-contractors-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  if (viewMode === 'edit-dashboard' && selectedContractor) {
    return (
      <AdminEditContractorDashboard
        contractorId={selectedContractor.id}
        onNavigate={(screen: string) => {
          if (screen === 'admin-contractors-list') {
            handleBackToList();
          } else {
            onNavigate?.(screen);
          }
        }}
      />
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div className="content">
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ fontSize: 28 }}>Contractors</h1>
              <p style={{ color: '#6b7280', marginTop: 8 }}>Manage contractor accounts and verification status</p>
            </div>
            <button
              onClick={() => onNavigate?.('admin-contractor-add')}
              className="btn btn-primary"
              style={{ fontSize: 14, padding: '10px 20px' }}
            >
              Add Contractor +
            </button>
          </div>
        </div>

        {/* Filters */}
        <SettingsCard title="Search & Filter Contractors">
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <FaSearch style={{ width: 20, height: 20, color: '#9ca3af' }} />
                </div>
                <input
                  type="text"
                  placeholder="Search contractors by company or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                  style={{ paddingLeft: 40 }}
                />
              </div>
            </div>
            <div style={{ minWidth: 200 }}>
              <select
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
                className="form-control"
              >
                <option value="">All Contractors</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
            >
              Search
            </button>
          </form>
        </SettingsCard>

        {/* Error Message */}
        {error && (
          <div style={{ 
            background: '#fef2f2', 
            border: '1px solid #fecaca', 
            borderRadius: 8, 
            padding: 16, 
            marginBottom: 24 
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: 12 }}>
                <svg style={{ width: 20, height: 20, color: '#f87171' }} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 style={{ fontSize: 14, fontWeight: '500', color: '#991b1b', margin: 0 }}>Error</h3>
                <p style={{ fontSize: 14, color: '#b91c1c', margin: '4px 0 0 0' }}>{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* CB:BEGIN bulk-operations-panel */}
        {contractors.length > 0 && (
          <BulkOperationsPanel
            selectedIds={selectedContractorIds}
            entityType="contractors"
            onSuccess={() => {
              setSelectedContractorIds([]);
              fetchContractors(currentPage, searchTerm, verificationFilter);
            }}
            onClearSelection={() => setSelectedContractorIds([])}
          />
        )}
        {/* CB:END bulk-operations-panel */}

        {/* Contractors List */}
        <SettingsCard title="All Contractors">
          {contractors.length > 0 ? (
            <div className="space-y-4">
              {contractors.map((contractor) => (
                <div key={contractor.id} className="project-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', minHeight: '80px', overflow: 'visible' }}>
                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    {/* CB:BEGIN bulk-selection-checkbox */}
                    <input
                      type="checkbox"
                      checked={selectedContractorIds.includes(contractor.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContractorIds([...selectedContractorIds, contractor.id]);
                        } else {
                          setSelectedContractorIds(selectedContractorIds.filter(id => id !== contractor.id));
                        }
                      }}
                      style={{ marginRight: 16, width: 18, height: 18, cursor: 'pointer' }}
                    />
                    {/* CB:END bulk-selection-checkbox */}
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: 16
                    }}>
                      <span style={{ fontSize: 16, fontWeight: '600', color: 'white' }}>
                        {contractor.company?.[0] || contractor.user?.email?.[0]?.toUpperCase() || 'C'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: '600', color: '#111827', marginBottom: 4 }}>
                          {contractor.company || 'Unnamed Company'}
                        </div>
                        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 2 }}>
                          {contractor.user?.email || 'N/A'}
                        </div>
                        {contractor.user?.phone && (
                          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 2 }}>
                            {contractor.user.phone}
                          </div>
                        )}
                        <div style={{ fontSize: 14, color: '#9ca3af' }}>
                          Joined {contractor.user?.createdAt ? formatDate(contractor.user.createdAt) : 'N/A'}
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditContractorProfile(contractor)}
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '6px 12px', height: 'fit-content', whiteSpace: 'nowrap' }}
                      >
                        Edit Profile
                      </button>
                      <button
                        onClick={() => {
                          setSelectedContractorForDocuments(contractor);
                          setDocumentsModalOpen(true);
                        }}
                        className="btn btn-secondary"
                        style={{ fontSize: 12, padding: '6px 12px', height: 'fit-content', whiteSpace: 'nowrap' }}
                      >
                        View Documents
                      </button>

                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{
                          padding: '4px 8px',
                          fontSize: 12,
                          fontWeight: '600',
                          borderRadius: 999,
                          background: contractor.verified ? '#d1fae5' : '#fef3c7',
                          color: contractor.verified ? '#065f46' : '#92400e'
                        }}>
                          {contractor.verified ? 'Verified' : 'Unverified'}
                        </span>

                      </div>
                      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>
                        {contractor._count.bids} bids
                      </div>
                      {contractor.verification?.licenseNumber && (
                        <div style={{ fontSize: 14, color: '#6b7280' }}>
                          License: {contractor.verification.licenseNumber}
                        </div>
                      )}
                      {/* CB:BEGIN stripe-connect-status */}
                      <div style={{ marginTop: 4 }}>
                        <span style={{
                          padding: '2px 6px',
                          fontSize: 11,
                          fontWeight: '600',
                          borderRadius: 4,
                          background: contractor.stripeAccountId ? '#d1fae5' : '#fef3c7',
                          color: contractor.stripeAccountId ? '#065f46' : '#92400e'
                        }}>
                          {contractor.stripeAccountId ? '✓ Stripe Connected' : '⚠ No Stripe'}
                        </span>
                      </div>
                      {/* CB:END stripe-connect-status */}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => handleViewContractor(contractor)}
                        className="btn btn-primary"
                        style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}
                        title="View contractor dashboard and enter their session"
                      >
                        View / Enter Session
                      </button>
                      {/* CB:BEGIN bid-fee-badge */}
                      {contractor.bidFeeOverride && contractor.bidFeeOverride !== 'DEFAULT' && (
                        <span style={{
                          fontSize: 11,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontWeight: '600',
                          backgroundColor: contractor.bidFeeOverride === 'NO_BID_FEE' ? '#10b981' : '#dc2626',
                          color: 'white',
                          whiteSpace: 'nowrap'
                        }}>
                          {contractor.bidFeeOverride === 'NO_BID_FEE' ? 'No bid fee' : 'Bid fee'}
                        </span>
                      )}
                      {/* CB:END bid-fee-badge */}
                    </div>
                    <button
                      onClick={() => handleEditContractorDashboard(contractor)}
                      className="btn btn-secondary"
                      style={{ fontSize: 12, padding: '6px 12px', whiteSpace: 'nowrap' }}
                      title="Edit contractor profile data, verification, and settings"
                    >
                      Edit Data
                    </button>
                    <button
                      onClick={() => handleDeleteContractor(contractor)}
                      className="btn"
                      style={{ 
                        fontSize: 12, 
                        padding: '6px 12px', 
                        whiteSpace: 'nowrap',
                        backgroundColor: '#dc2626',
                        color: 'white',
                        border: 'none'
                      }}
                    >
                      <FaTrash style={{ marginRight: 4 }} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
              <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>🔧</div>
              <h3 style={{ fontSize: 16, fontWeight: '500', color: '#111827', margin: '0 0 8px 0' }}>No contractors found</h3>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                {searchTerm || verificationFilter ? 'Try adjusting your search criteria.' : 'No contractors have registered yet.'}
              </p>
            </div>
          )}
        </SettingsCard>

        {/* Pagination */}
        {totalPages > 1 && (
          <SettingsCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
                  Showing page <span style={{ fontWeight: '500' }}>{currentPage}</span> of{' '}
                  <span style={{ fontWeight: '500' }}>{totalPages}</span>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
          </SettingsCard>
        )}

        {/* CB:BEGIN contractor-documents-modal */}
        {selectedContractorForDocuments && (
          <ContractorDocumentsModal
            contractorId={selectedContractorForDocuments.id}
            contractorName={
              selectedContractorForDocuments.company ||
              selectedContractorForDocuments.user?.email ||
              undefined
            }
            isOpen={documentsModalOpen}
            onClose={() => {
              setDocumentsModalOpen(false);
              setSelectedContractorForDocuments(null);
            }}
          />
        )}
        {/* CB:END contractor-documents-modal */}
        
        {/* CB:BEGIN import-bids-modal */}
        {selectedContractorForBids && (
          <ImportBidsModal
            contractorId={selectedContractorForBids.id}
            contractorName={selectedContractorForBids.company || selectedContractorForBids.user?.email || 'Contractor'}
            isOpen={importBidsModalOpen}
            onClose={() => {
              setImportBidsModalOpen(false);
              setSelectedContractorForBids(null);
            }}
            onMigrated={() => {
              // Refresh contractors list to update bid counts
              fetchContractors(currentPage, searchTerm, verificationFilter);
            }}
          />
        )}
        {/* CB:END import-bids-modal */}
      </div>
    </ResponsiveContainer>
  );
};

export default AdminContractorsList;
// CB:END admin-contractors-list

